import type { HostedFlowStep } from "@portal/app/_server/services/formFunnel/payloadTypes";
import type { ProgramRequiredField } from "@portal/app/_common/types/program";
import type { DobFieldVisibility } from "@portal/app/_common/types/program";
import { captureMessage } from "@sentry/nextjs";

/**
 * Maps program requiredFields to the hosted flow steps that collect them.
 * Returns only the data-collection steps (no 'contact' or 'confirm').
 */
function buildStepsFromRequiredFields(
  requiredFields?: ProgramRequiredField[] | null
): HostedFlowStep[] {
  if (requiredFields == null || requiredFields.length === 0) return [];
  const steps: HostedFlowStep[] = [];

  // Eligibility questions
  if (requiredFields.includes('IS_US_CITIZEN')) steps.push('isUsCitizen');
  if (requiredFields.includes('GRADUATED_HIGH_SCHOOL_IN')) steps.push('graduatedHighSchoolIn');
  if (requiredFields.includes('HAS_RN_LICENSE')) steps.push('hasRNLicense');
  if (requiredFields.includes('HAS_MILITARY_AFFILIATION')) steps.push('hasMilitaryAffiliation');

  // Phone
  if (requiredFields.includes('PHONE')) steps.push('phone');

  // Address: ADDRESS_FULL takes precedence over ADDRESS_ZIP
  if (requiredFields.includes('ADDRESS_FULL')) steps.push('address');
  else if (requiredFields.includes('ADDRESS_ZIP')) steps.push('addressZip');

  return steps;
}

/**
 * Returns true when the program's DOB field is either REQUIRED or OPTIONAL.
 * HIDDEN programs skip the step entirely. When OPTIONAL, the step still
 * renders but allows the user to skip without providing a value (see
 * HostedDynamicStep's optional handling).
 */
function shouldIncludeDobStep(dobFieldVisibility: DobFieldVisibility): boolean {
  return dobFieldVisibility === "REQUIRED" || dobFieldVisibility === "OPTIONAL";
}

/**
 * Derives which hosted flow steps to show based on a matched program's
 * requiredFields and configured dobFieldVisibility.
 *
 * When `configuredSteps` is provided (from PayloadCMS hostedFlowConfig.steps),
 * it is treated as a **superset** of steps the funnel author is willing to
 * collect — for each match, we filter that list down to only the steps the
 * matched program actually needs (always keeping `contact` and `confirm`),
 * and add any required steps the CMS author forgot with a Sentry warning.
 *
 * Step ordering (when deriving from scratch):
 *   1. 'contact' (always: collects name + email)
 *   2. Eligibility questions (isUsCitizen, graduatedHighSchoolIn, hasRNLicense, hasMilitaryAffiliation)
 *   3. 'phone' (if PHONE required)
 *   4. 'addressZip' (if ADDRESS_ZIP required but NOT ADDRESS_FULL)
 *   5. 'address' (if ADDRESS_FULL required)
 *   6. 'dob' (if dobFieldVisibility is REQUIRED or OPTIONAL)
 *   7. 'confirm' (always: final review + disclaimer acceptance)
 */
export function deriveHostedFlowSteps(
  requiredFields: ProgramRequiredField[] | null | undefined,
  configuredSteps: HostedFlowStep[] | undefined,
  dobFieldVisibility: DobFieldVisibility,
): HostedFlowStep[] {
  const includeDob = shouldIncludeDobStep(dobFieldVisibility);

  // If explicit steps are configured in PayloadCMS, treat them as a
  // superset of "willing" steps and filter down to what the matched
  // program actually needs. 'contact' and 'confirm' are always kept;
  // everything else is kept only if it appears in the program's
  // required-field-derived set (or 'dob' when explicitly required).
  if (configuredSteps && configuredSteps.length > 0) {
    const neededDataSteps = new Set<HostedFlowStep>(
      buildStepsFromRequiredFields(requiredFields)
    );
    if (includeDob) neededDataSteps.add('dob');

    const keepStep = (s: HostedFlowStep) =>
      s === 'contact' || s === 'confirm' || neededDataSteps.has(s);

    const filtered = configuredSteps.filter(keepStep);

    // Safety net: if the CMS author left a program-required step out of
    // the configuredSteps list, add it after 'contact' so the lead is not
    // submitted missing data. This preserves the existing fail-safe added
    // when the configuredSteps override was first introduced.
    const missing = [...neededDataSteps].filter((s) => !filtered.includes(s));
    if (missing.length > 0) {
      captureMessage(
        `[deriveHostedFlowSteps] configuredSteps missing program-required steps: ${missing.join(', ')}. Auto-adding.`,
        { level: "warning" }
      );
      const contactIdx = filtered.indexOf('contact');
      const insertAt = contactIdx >= 0 ? contactIdx + 1 : 0;
      filtered.splice(insertAt, 0, ...missing);
    }

    // Ensure contact/confirm bookends are present even if the CMS author
    // omitted them.
    if (!filtered.includes('contact')) filtered.unshift('contact');
    if (!filtered.includes('confirm')) filtered.push('confirm');

    return filtered;
  }

  // No CMS override — derive purely from the matched program.
  // If requiredFields is not provided (null/undefined), use default steps
  // that cover the universal lead-form fields.
  if (requiredFields == null) {
    const steps: HostedFlowStep[] = ['contact', 'phone', 'address'];
    if (includeDob) steps.push('dob');
    steps.push('confirm');
    return steps;
  }

  // If requiredFields is an empty array, show only contact + confirm
  // (bare minimum), plus DOB only if the program explicitly requires it.
  if (requiredFields.length === 0) {
    const steps: HostedFlowStep[] = ['contact'];
    if (includeDob) steps.push('dob');
    steps.push('confirm');
    return steps;
  }

  // Build steps based on requiredFields
  const steps: HostedFlowStep[] = ['contact', ...buildStepsFromRequiredFields(requiredFields)];

  // DOB step — driven by program.dobFieldVisibility
  // (not requiredFields, since DOB has its own program-level setting).
  if (includeDob) steps.push('dob');

  // Warn about any requiredFields values that this function doesn't handle
  const knownFields = new Set<ProgramRequiredField | string>([
    'NAME_FIRST', 'NAME_LAST', 'EMAIL',
    'IS_US_CITIZEN', 'GRADUATED_HIGH_SCHOOL_IN', 'HAS_RN_LICENSE',
    'HAS_MILITARY_AFFILIATION', 'PHONE', 'ADDRESS_FULL', 'ADDRESS_ZIP',
  ]);
  const unrecognized = requiredFields.filter(f => !knownFields.has(f));
  if (unrecognized.length > 0) {
    captureMessage(
      `[deriveHostedFlowSteps] Unrecognized requiredFields: ${unrecognized.join(', ')}`,
      { level: "warning" }
    );
  }

  steps.push('confirm');

  return steps;
}
